import { useLocation, useParams } from "react-router-dom";
import { useMemo } from "react";
import { IHARSearchListingsFilters } from "types/filters/search-listings-filters";
import { isListingPath } from "utils/routes";

/**
 * The backend injects a JSON payload into a non-executed
 * `<script type="application/json" id="server-input">` tag (see public/index.html).
 * Reading it from a JSON tag instead of an inline `window._serverInput = {...}`
 * keeps the unreplaced `%SERVER_INPUT%` placeholder from being a JS syntax error
 * when CRA's dev server serves the page without backend substitution.
 */
function readServerInput(): { question?: string } {
  try {
    const el = document.getElementById("server-input");
    return el?.textContent ? JSON.parse(el.textContent) : {};
  } catch {
    return {};
  }
}

let serverInput = readServerInput();

/**
 * The injected question is one-shot: once it has been auto-sent, clear it so it
 * can't be re-sent when the user later navigates client-side to another
 * new-chat route (where there is no `?question=` param to clear).
 */
export function consumeServerInput(): void {
  serverInput = {};
}

type QueryFlags = {
  embed: boolean;
  readOnly: boolean;
  hideUser: boolean;
  hideHeader: boolean;
  hideFooter: boolean;
  hideShare: boolean;
  timeline: boolean;
  messageId?: string;
  token?: string;
  session?: string;
  source?: string;
  isContentNotVisible?: boolean | string;
  search: string;
  // userId: string | null;
  // violation: string | null;
  // rating: string | null;

  /** Decoded ?question=... only when there is NO :session in the URL; otherwise null */
  initialQuestion: string | null;

  /** True when NOT on /listing/... routes (General Assistant) */
  isGeneralAssistant: boolean;

  /** True when ?listing_filter= is present in URL and we're on General Assistant */
  hasListingFilter: boolean;

  /** Parsed pairs from decoded listing_filter (only when hasListingFilter), else null */
  listingFilters: IHARSearchListingsFilters | null;

  /** True when display_error_msg=1 is present in URL */
  displayErrorMsg: boolean;

  /** Decoded ?q=... only when there is NO :session in the URL; otherwise null */
  question?: string | null;

  cId?: string;
};

export function useQueryFlags(): QueryFlags {
  const { search, pathname } = useLocation();
  const { token, session } = useParams();

  return useMemo(() => {
    const params = new URLSearchParams(search);

    const asBool = (value: string | null) =>
      /^(1|true|yes)$/i.test(value ?? "");

    const decode = (value: string | null) => {
      if (!value) return null;
      // NOTE: `value` already comes from URLSearchParams.get(), which has
      // percent-decoded once and converted '+' to space. Decoding again here
      // corrupts values that legitimately contain percent-escapes — e.g. the
      // encoded map_tools_polygon WKT inside the question's markdown link
      // (POLYGON%20%28%28... would become "POLYGON ((..." with raw spaces,
      // splitting the link href). So just trim; do not decode a second time.
      return value.trim() || null;
    };

    const readOnly = asBool(params.get("readonly"));
    const question = params.get("question");
    // The backend can inject a question into the page (#server-input) instead of
    // passing it as ?question=, which avoids the AWS WAF blocking long
    // polygon/bounds payloads in the redirect URL. The URL param keeps priority;
    // the injected value is the fallback and is consumed once (see
    // consumeServerInput) after it is auto-sent.
    const serverQuestion = serverInput.question ?? null;
    const initialQuestion = !session
      ? decode(question) ?? decode(serverQuestion)
      : null;
    const isGeneralAssistant = !isListingPath(pathname);
    const rawListingFilter = params.get("listing_filter");
    const hasListingFilter = isGeneralAssistant && !!rawListingFilter;
    const q = !session ? decode(params.get("q")) : null;
    let listingFilters: IHARSearchListingsFilters | null = null;

    if (hasListingFilter) {
      try {
        // decode once for the outer param
        const decoded = decodeURIComponent(rawListingFilter!);
        // then parse as its own querystring (key=value&key2=value2...)
        const inner = new URLSearchParams(decoded);

        // A key may repeat (`property_class_id[]=1&property_class_id[]=2&...`),
        // so read every occurrence with getAll: iterating with forEach and
        // assigning into a flat object keeps only the LAST value and silently
        // drops the rest. Keep the raw key (`[]` suffix included) — stripping
        // and merging the plain/`[]` variants is normalizeNativeFilters' job
        // (operations/searches/search-filter-operation-model.ts).
        //
        // A key seen once stays a scalar string: normalizeNativeFilters folds
        // arrays into a comma-separated string, which would corrupt a single
        // value that legitimately contains commas (map_tools_polygon's WKT,
        // streetaddress, subdivisions, ...).
        const out: Record<string, string | string[]> = {};
        for (const key of new Set(inner.keys())) {
          // decodeURIComponent again to clean nested encodes in values
          const values = inner.getAll(key).map((value) => {
            try {
              return decodeURIComponent(value);
            } catch {
              return value;
            }
          });
          out[key] = values.length > 1 ? values : values[0];
        }

        listingFilters = out as unknown as IHARSearchListingsFilters;
      } catch {
        listingFilters = null;
      }
    }

    return {
      embed: asBool(params.get("embed")),
      hideUser: asBool(params.get("hide_user")),
      hideHeader: asBool(params.get("hide_header")),
      hideFooter: asBool(params.get("hide_footer")),
      timeline: asBool(params.get("timeline")),
      hideShare: asBool(params.get("hide_share")),
      // userId: params.get("user_id"),
      // violation: params.get("violation"),
      // rating: params.get("rating"),
      messageId: params.get("message_id") || undefined,
      cId: params.get("cid") || undefined,
      readOnly,
      token,
      session,
      source: params.get("source") || undefined,
      isContentNotVisible: readOnly || token,
      search,
      initialQuestion,
      isGeneralAssistant,
      hasListingFilter,
      listingFilters,
      displayErrorMsg: asBool(params.get("display_error_msg")),
      question: q,
      // userId: string | null;
      // violation: string | null;
      // rating: string | null;
    };
  }, [search, token, session, pathname]);
}
